import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Routes from './Routes';
import {
  brownoutMessageSelector,
  requestIdVerificationModalIsOpenSelector,
  userIdIreneSelector,
} from '../../../selectors';
import { getApplicationsTabPath, getLandlordReviewPath, getMissingPermissionsPath } from '../../../constants/urls';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import { useQuery } from '@apollo/client';
import useQueryVariables from '../../../hooks/useQueryVariables';

import {
  APPLICATION_MANAGER_QUERY,
  getApplicationManagerNeedsToIdVerify,
  getNeedsApplicationManager,
} from '../../../utils/businessVerification';
import { receiveBusinessVerificationData } from '../../../actions/businessVerificationActions';
import RequestIdVerificationModal from './RequestIdVerificationModal';
import {
  cancelApplicationManagerIdvRequest,
  requestApplicationManagerToIdv,
} from '../../../actions/initApplicationActionCreators';
import LandlordReviewWrapper from '../../../components/LandlordReviewWrapper';

function LandlordReview({ initLandlordReview }) {
  const dispatch = useDispatch();
  const userIdIrene = useSelector(userIdIreneSelector);
  const isRequestToIdVerifyModalOpen = useSelector(requestIdVerificationModalIsOpenSelector);
  const history = useHistory();
  const { isExact: isAtLandlordReviewPage } = useRouteMatch();
  const brownoutMessage = useSelector(brownoutMessageSelector);
  const { propertyId, applicationId } = useQueryVariables();
  const applicationsTabPath = getApplicationsTabPath(propertyId);
  const { data, refetch: refetchApplicationManagerQuery } = useQuery(APPLICATION_MANAGER_QUERY, {
    variables: { propertyId },
    onCompleted(completedData) {
      dispatch(receiveBusinessVerificationData(completedData, userIdIrene));
    },
  });

  const needsApplicationManager = getNeedsApplicationManager(data);
  const needsApplicationManagerToIdVerify = getApplicationManagerNeedsToIdVerify(data, userIdIrene);
  useEffect(() => {
    if ((needsApplicationManager || needsApplicationManagerToIdVerify) && !isAtLandlordReviewPage) {
      history.push(getLandlordReviewPath(propertyId, applicationId));
      // Can be removed after https://zbrt.atl.zillow.net/browse/RA-2524
      document.body.classList.remove('ReactModal__Body--open');
      if (needsApplicationManagerToIdVerify) {
        dispatch(requestApplicationManagerToIdv());
      } else {
        history.push(getMissingPermissionsPath(propertyId));
      }
    }
  }, [
    history,
    isAtLandlordReviewPage,
    applicationId,
    propertyId,
    needsApplicationManager,
    needsApplicationManagerToIdVerify,
    dispatch,
  ]);

  useEffect(() => {
    initLandlordReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (brownoutMessage) {
      history.replace(applicationsTabPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brownoutMessage]);

  function handleRequestIdVerificationModalClose() {
    dispatch(cancelApplicationManagerIdvRequest());
  }

  return (
    <StyledContainer backgroundColor="zrmBackground" className="App__wrapper">
      <RequestIdVerificationModal
        isOpen={isRequestToIdVerifyModalOpen}
        onClose={handleRequestIdVerificationModalClose}
        employees={data?.user?.userManagementCompany?.employees}
        refetchApplicationManagerQuery={refetchApplicationManagerQuery}
      />
      <LandlordReviewWrapper role="main" paddingX={{ md: 'md' }} marginBottom="xl">
        <Routes />
      </LandlordReviewWrapper>
    </StyledContainer>
  );
}

LandlordReview.propTypes = {
  loadingScreenStatus: PropTypes.object,
  initLandlordReview: PropTypes.func.isRequired,
};

export default LandlordReview;
